if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          
          try {
            this.updateURL(productUrl, variant?.id);
          } catch(e) { console.error("Error updating URL:", e); }

          const postProcessCallbacks = [
            ...this.postProcessHtmlCallbacks,
            (newNode) => {
              const newProductInfo = newNode.tagName === 'PRODUCT-INFO' ? newNode : newNode.querySelector('product-info');
              if (newProductInfo) {
                try {
                  newProductInfo.updateVariantInputs(variant);
                } catch(e) { console.error("Error updating variant inputs:", e); }
                
                try {
                  const poContainers = newProductInfo.querySelectorAll('.rsProductOfferContainer');
                  let newItemId = variant?.sku || '';
                  if (!newItemId && html) {
                    const skuEl = html.querySelector('[data-product-model]') || html.querySelector('#VariantSKU-' + this.sectionId);
                    if (skuEl) {
                      newItemId = skuEl.textContent.replace(/Model:\s*/i, '').trim();
                    }
                  }
                  const absoluteProductUrl = new URL(productUrl, window.location.origin).toString();

                  poContainers.forEach(c => {
                    if (window.Rs && window.Rs.Events) {
                      const rsEvent = window.Rs.Events.createEvent(
                        window.Rs.Events.UPDATE_PRODUCT_OFFER_CONTAINER_WITH_NEW_ITEM
                      );
                      rsEvent.newItemId = newItemId;
                      rsEvent.newItemUrl = absoluteProductUrl;
                      c.dispatchEvent(rsEvent);
                    }

                    c.setAttribute('data-rs-item-id', newItemId);
                    c.setAttribute('data-rs-item-url', absoluteProductUrl);

                    const input = c.querySelector('input[name="rsItemId"]');
                    if (input) {
                      input.value = newItemId;
                    }
                    const urlInput = c.querySelector('input[name="rsItemUrl"]');
                    if (urlInput) {
                      urlInput.value = absoluteProductUrl;
                    }
                  });
                } catch(e) { console.error("Error updating RS offer containers during swap:", e); }
              }
            }
          ];

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              postProcessCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              postProcessCallbacks
            );
          }
        };
      }

     renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;

            const html = new DOMParser().parseFromString(responseText, 'text/html');

            if (typeof callback === 'function') callback(html);
          })
          .then(() => {
            // ✅ handle element OR id string
            if (!targetId) return;

            // If already an element
            if (targetId instanceof Element) {
              targetId.focus?.();
              return;
            }

            // If it's a selector like "#Option-..."
            if (typeof targetId === 'string') {
              const cleanId = targetId.replace(/^#/, '').trim();
              if (!cleanId) return;

              document.getElementById(cleanId)?.focus();
            }
          })
          .catch((error) => {
            if (error?.name === 'AbortError') {
              console.log('Fetch aborted by user');
              return;
            }

            console.error(error);
          });
      }


      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          
          try {
            this.pickupAvailability?.update(variant);
          } catch(e) { console.error("Error updating pickup availability:", e); }

          try {
            this.updateOptionValues(html);
          } catch(e) { console.error("Error updating option values:", e); }

          try {
            this.updateURL(productUrl, variant?.id);
          } catch(e) { console.error("Error updating URL:", e); }

          try {
            this.updateVariantInputs(variant);
          } catch(e) { console.error("Error updating variant inputs:", e); }

          if (!variant) {
            this.setUnavailable();
            return;
          }
          const absoluteProductUrl = new URL(productUrl, window.location.origin).toString();

          // Selected option element (dropdown OR radio OR fallback)
          let selectedOption = null;
          try {
            // 1️⃣ Dropdown
            const selectEl = this.querySelector('.product-form__input--dropdown select');
            if (selectEl) {
              selectedOption = selectEl.options[selectEl.selectedIndex];
            } else {
              // 2️⃣ Radio / swatch
              const checkedRadio = this.querySelector(
                'input[type="radio"]:checked, .swatch-input__input:checked'
              );

              if (checkedRadio) {
                selectedOption = checkedRadio;
              }
            }

            // 3️⃣ Fallback → no select & no radio (single variant product)
            if (!selectedOption) {
              // Use product form or a hidden variant input as fallback
              selectedOption =
                this.querySelector('input[name="id"]') || // Shopify variant ID input
                this.querySelector('form') ||              // product form
                this;                                      // last-resort fallback
            }

            // ✅ Set attributes on selectedOption safely (if any other scripts use it)
            if (selectedOption && typeof selectedOption.setAttribute === 'function') {
              selectedOption.setAttribute('data-rs-new-item-id', variant?.sku || '');
              selectedOption.setAttribute('data-rs-new-item-url', absoluteProductUrl);
            }
          } catch (e) {
            console.error("Error setting option attributes:", e);
          }

          // Read SKU directly from variant object, fallback to DOM SKU, to avoid viewTransition race conditions
          let newItemId = variant?.sku || '';
          if (!newItemId && html) {
            try {
              const skuEl = html.querySelector('[data-product-model]') || html.querySelector('#VariantSKU-' + this.sectionId);
              if (skuEl) {
                newItemId = skuEl.textContent.replace(/Model:\s*/i, '').trim();
              }
            } catch (e) {
              console.error("Error extracting SKU from HTML:", e);
            }
          }
          const newItemUrl = absoluteProductUrl;

          try {
            this.updateMedia(html, variant?.featured_media?.id);
          } catch (e) {
            console.error("Error updating media:", e);
          }

          try {
            const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
              const source = html.getElementById(`${id}-${this.sectionId}`);
              const destination = this.querySelector(`#${id}-${this.dataset.section}`);
              if (source && destination) {
                destination.innerHTML = source.innerHTML;
                destination.classList.toggle('hidden', shouldHide(source));
              }
            };

            updateSourceFromDestination('price');
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
            updateSourceFromDestination('Volume');
            updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Specifications');
            updateSourceFromDestination('Features');
            updateSourceFromDestination('VariantDescription');
            updateSourceFromDestination('VariantSKU');
          } catch (e) {
            console.error("Error updating details fields:", e);
          }

          try {
            this.updateQuantityRules(this.sectionId, html);
            this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
            this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
          } catch (e) {
            console.error("Error updating quantity rules:", e);
          }

          try {
            this.productForm?.toggleSubmitButton(
              html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
              window.variantStrings.soldOut
            );
          } catch (e) {
            console.error("Error updating submit button:", e);
          }

          try {
            const poContainers = document.querySelectorAll('.rsProductOfferContainer');
            console.log('poContainers:', poContainers, poContainers.length);

            poContainers.forEach(c => {
              if (window.Rs && window.Rs.Events) {
                const rsEvent = window.Rs.Events.createEvent(
                  window.Rs.Events.UPDATE_PRODUCT_OFFER_CONTAINER_WITH_NEW_ITEM
                );
                rsEvent.newItemId = newItemId;
                rsEvent.newItemUrl = newItemUrl;
                c.dispatchEvent(rsEvent);
              }

              c.setAttribute('data-rs-item-id', newItemId);
              c.setAttribute('data-rs-item-url', newItemUrl);

              const input = c.querySelector('input[name="rsItemId"]');
              if (input) {
                input.value = newItemId;
              }
              const urlInput = c.querySelector('input[name="rsItemUrl"]');
              if (urlInput) {
                urlInput.value = newItemUrl;
              }
            });
          } catch (e) {
            console.error("Error updating RS offer containers:", e);
          }

          try {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });
          } catch (e) {
            console.error("Error publishing variant change event:", e);
          }
        };
      }

      updateVariantInputs(variant) {
        this.querySelectorAll('input[name="id"]').forEach((input) => {
          input.value = variant?.id ?? '';
          if (variant && variant.available) {
            input.removeAttribute('disabled');
          } else {
            input.setAttribute('disabled', 'disabled');
          }
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId && html) {
          const firstSlide = html.querySelector('media-gallery ul li[data-media-id]');
          if (firstSlide) {
            const fullMediaId = firstSlide.getAttribute('data-media-id');
            const parts = fullMediaId.split('-');
            variantFeaturedMediaId = parts[parts.length - 1];
          }
        }
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        const mediaGallery = this.querySelector('media-gallery');

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // Update thumbnails list if present
        if (mediaGallery && mediaGallery.elements && mediaGallery.elements.thumbnails) {
          const sourceThumbnails = html.querySelector('media-gallery .thumbnail-list');
          const destThumbnails = mediaGallery.querySelector('.thumbnail-list');
          if (sourceThumbnails && destThumbnails) {
            destThumbnails.innerHTML = sourceThumbnails.innerHTML;

            // Re-bind click events on the new thumbnail items
            mediaGallery.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector('button');
              if (button) {
                button.addEventListener('click', mediaGallery.setActiveMedia.bind(mediaGallery, mediaToSwitch.dataset.target, false));
              }
            });

            // Reset thumbnail slider pages
            mediaGallery.elements.thumbnails.resetPages?.();
          }
        }

        // set featured media as active in the media gallery
        mediaGallery?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated || !this.quantityForm) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}


// Tabs Info 

document.addEventListener("DOMContentLoaded", function() {
  const buttons = document.querySelectorAll(".pro-tab-buttons li");
  const panes = document.querySelectorAll(".pro-tab-pane");
  const indicator = document.querySelector(".tab-indicator");

  function moveIndicator(activeBtn) {
    indicator.style.width = activeBtn.offsetWidth + "px";
    indicator.style.left = activeBtn.offsetLeft + "px";
  }

  const firstActive = document.querySelector(".pro-tab-buttons li.active");
  if (firstActive) moveIndicator(firstActive);

  buttons.forEach(btn => {
    btn.addEventListener("click", () => {
      buttons.forEach(b => b.classList.remove("active"));
      panes.forEach(p => p.classList.remove("active"));

      btn.classList.add("active");
      document.getElementById(btn.dataset.tab).classList.add("active");

      moveIndicator(btn);
    });
  });
});
